import { memo, useCallback, useMemo, useState } from "react";

import { cn } from "@/lib/chillnn/cn";
import { isPastDate } from "@/lib/dayjs/is-past-date";

import { stockRemainNumZodSchema } from "@/components/features/_shared/validation-schema/validation-schema";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";

import { DailyItemHoverDangerCard } from "./hover-danger-card";
import { DailyItemHoverWarningCard } from "./hover-warning-card";

type Props = {
	stockDailyItem: {
		date: string;
		/**
		 * 販売可能かどうか
		 */
		isAvailable: boolean;

		/**
		 * 編集可能かどうか
		 */
		isEditable?: boolean;

		/**
		 * 残在庫数（販売可能数 - 販売済み数）
		 */
		remainNum: number;

		/**
		 * 販売済み数
		 */
		soldNum: number;

		/**
		 * 価格設定済みかどうか
		 *
		 * 部屋の在庫設定画面でしか存在しない
		 */
		isSetPrice?: boolean;
	};

	currentStockDailyItem:
		| {
				/**
				 * 販売可能かどうか
				 */
				isAvailable: boolean;

				/**
				 * 残在庫数（販売可能数 - 販売済み数）
				 */
				remainNum: number;
		  }
		| undefined;

	onSetStock: (isAvailable: boolean, remainNum: number) => void;

	// 料金設定ページへのリンク
	roomPriceEditPagePath?: string;
};

export const StockTableRowDailyItem = memo(
	({
		stockDailyItem: { date, remainNum, soldNum, isAvailable, isEditable, isSetPrice },
		roomPriceEditPagePath,
		onSetStock,
		currentStockDailyItem,
	}: Props) => {
		const [isAvailableLocal, setIsAvailableLocal] = useState(currentStockDailyItem?.isAvailable ?? isAvailable);
		const [remainNumLocal, setRemainNumLocal] = useState(currentStockDailyItem?.remainNum ?? remainNum);
		const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

		const handleClickIsAvailable = useCallback(() => {
			setIsAvailableLocal(!isAvailableLocal);
			onSetStock(!isAvailableLocal, remainNumLocal);
		}, [onSetStock, remainNumLocal, isAvailableLocal]);

		const handleChangeRemainNum = useCallback(
			(remainNum: number | undefined) => {
				setRemainNumLocal(remainNum!);
				onSetStock(isAvailableLocal, remainNum!);

				const result = stockRemainNumZodSchema.safeParse(remainNum);
				if (result.success) {
					setErrorMessage(undefined);
				} else {
					setErrorMessage(result.error.errors[0].message);
				}
			},
			[onSetStock, isAvailableLocal],
		);

		// 料金が設定されていない場合はアラートを表示（現状部屋でしか日ごとに料金設定できないので、部屋在庫管理画面以外で表示されることはない）
		const shouldShowAlert = isSetPrice === false;

		const inputDisabled = useMemo(() => {
			if (isPastDate(date)) {
				return true;
			}

			if (typeof isEditable === "boolean") {
				return !isEditable;
			}

			return false;
		}, [isEditable, date]);

		const showGrayScale = useMemo(() => {
			if (typeof isEditable === "boolean") {
				return !isEditable;
			}

			if (isPastDate(date)) {
				return true;
			}

			return false;
		}, [date, isEditable]);

		const buttonText = useMemo<"在庫無" | "売止" | "販売中" | "不可">(() => {
			if (isPastDate(date)) {
				return "不可";
			}
			if (typeof isEditable === "boolean" && !isEditable) {
				return "売止";
			}
			if (typeof isEditable === "boolean" && isEditable && remainNumLocal === 0) {
				return "在庫無";
			}

			return isAvailableLocal ? "販売中" : "売止";
		}, [date, isAvailableLocal, isEditable, remainNumLocal]);

		const buttonVariant = useMemo<ButtonProps["variant"]>(() => {
			if (isPastDate(date)) {
				return "disabled";
			}
			if (typeof isEditable === "boolean" && !isEditable) {
				return "disabled";
			}
			if (typeof isEditable === "boolean" && isEditable && remainNumLocal === 0) {
				return "disabled";
			}

			return isAvailableLocal ? "status-in-sale" : "status-not-for-sale";
		}, [date, isEditable, isAvailableLocal, remainNumLocal]);

		return (
			<td
				className={cn(
					"relative h-16 max-h-16 border-r border-r-zinc-200 p-1.5 text-center last:border-none",
					isPastDate(date) ? "pointer-events-none" : "bg-white",
				)}
			>
				{!isPastDate(date) &&
					(shouldShowAlert ? (
						<DailyItemHoverWarningCard roomPriceEditPagePath={roomPriceEditPagePath} />
					) : !isEditable ? (
						<DailyItemHoverDangerCard />
					) : null)}

				<div className="space-y-2">
					<div>
						<InputNumberField
							value={remainNumLocal}
							handleInput={handleChangeRemainNum}
							textAlign={"center"}
							hiddenSpinButton={true}
							disabled={inputDisabled}
							errorMessage={errorMessage}
						/>
					</div>

					<p className="text-sm">{soldNum}</p>

					<Button onClick={handleClickIsAvailable} variant={buttonVariant} size="2xs" className="w-full">
						<p className="pl-0.5 text-xs">{buttonText}</p>
					</Button>
				</div>

				{/* 操作できない場合はグレーの背景を表示 */}
				{showGrayScale && <div className="absolute left-0 top-0 h-full w-full bg-zinc-200 opacity-70" />}
			</td>
		);
	},
);

StockTableRowDailyItem.displayName = "StockTableRowDailyItem";
